import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95 transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-[0_1px_2px_hsl(var(--primary)/0.3)] hover:shadow-[0_2px_8px_hsl(var(--primary)/0.4)] hover:bg-primary/95",
        destructive: "bg-destructive text-destructive-foreground shadow-[0_1px_2px_hsl(var(--destructive)/0.3)] hover:shadow-[0_2px_8px_hsl(var(--destructive)/0.4)] hover:bg-destructive/95",
        outline: "border-[1.5px] border-border bg-card/50 backdrop-blur-sm hover:bg-card hover:border-primary/30 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]",
        secondary: "bg-secondary text-secondary-foreground shadow-[0_1px_2px_hsl(var(--secondary)/0.3)] hover:shadow-[0_2px_8px_hsl(var(--secondary)/0.4)] hover:bg-secondary/90",
        ghost: "hover:bg-muted/50 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        ios: "bg-gradient-primary text-primary-foreground shadow-[0_2px_8px_hsl(var(--primary)/0.25)] hover:shadow-[0_4px_12px_hsl(var(--primary)/0.35)]",
      },
      size: {
        default: "h-11 px-5 py-2.5",
        sm: "h-9 rounded-lg px-3.5 text-xs",
        lg: "h-12 rounded-xl px-7 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
